import api from "../../../services/newApi/api";
import newTokenService from "./newTokenService";
import csrfService from "../api/csrfService";

export interface MfaChannel {
  type: "sms" | "whatsapp" | "email";
  masked: string;
  sent: boolean;
  error?: string;
}

export interface MFASession {
  mfaToken: string;
  userId: number;
  roleId: number;
  expiresAt: Date;
  /** Cellphone REAL — usar em payloads, NUNCA exibir direto na UI. */
  cellphone: string;
  /** Email REAL — uso interno. */
  email: string;
  /** Versão mascarada para display. */
  cellphoneMasked?: string | undefined;
  /** Versão mascarada para display. */
  emailMasked?: string | undefined;
  /** Canais retornados pelo backend após /auth/send-code (com status sent/error). */
  channels?: MfaChannel[] | undefined;
  /** Backend sinaliza cooldown — UI mostra countdown antes de permitir reenvio. */
  cooldownEndsAt?: Date | undefined;
  /** Tentativas restantes (decrementa em cada verify falho). */
  attemptsRemaining?: number | undefined;
  /** Quando definido, sessão está em lockout até essa data. */
  lockedUntil?: Date;
  /**
   * When a send-code last succeeded. In the unified flow the send answers `channels: []`, so the channels cannot say
   * whether a code is on its way; the screen shows the clock only while one is (P20EE-177).
   */
  codeSentAt?: Date | undefined;
  /**
   * Código OTP em claro, só presente quando o backend está em dev/test. Atalho
   * de teste exibido na tela de OTP; em hom/prod nunca chega (gate no backend).
   */
  devCode?: string | undefined;
}

export interface MFAVerificationResult {
  success: boolean;
  token?: string;
  refreshToken?: string;
  expiresAt?: number;
  user?: any;
  mfaSessionToken?: string;
  error?: string;
  type?: "expiredSession";
  next_step?: string;
  /** HTTP status da recusa (401 código inválido, 423 lockout) — o chamador precisa dele. */
  status?: number;
  /** Segundos até poder tentar de novo, quando o backend envia (lockout 423). */
  retryAfterSeconds?: number;
}

class MFAService {
  private mfaSession: MFASession | null = null;
  // Dedup do send-code: protege contra React.StrictMode dev double-fire e
  // chamadas paralelas (Reenviar clicado 2x rápido) que dispararia múltiplos
  // SMS/email/WhatsApp ao backend (custos $$ + UX ruim).
  private lastSendAttemptAt = 0;
  private static SEND_DEDUP_MS = 3000; // 3s — abaixo do cooldown backend (30s)

  setMFASession(session: MFASession) {
    this.mfaSession = session;
  }

  /**
   * Devolve a sessão MFA enquanto ela existir em memória — e NÃO a destrói quando
   * o código expira. `expiresAt` é o relógio de EXIBIÇÃO do código (5min, o
   * `ttlSeconds` do TOTP no backend), não o fim da sessão: o `mfaToken` dura 15min
   * (`mfaSessionExpiry`), então entre 5 e 15min ele ainda pede um novo código
   * (reenvio) e valida. Destruir a sessão aqui prendia o fluxo — cronômetro parado
   * em 00:00, "Reenviar" e "Validar" morrendo com "Sessão MFA não disponível",
   * sem saída a não ser recomeçar o login. P20EE-138.
   *
   * Validade do código é pergunta separada: `isMFASessionValid()` responde pelo
   * `expiresAt`, e o backend é a autoridade final (um `mfaToken` de fato expirado
   * devolve 401). A sessão só é limpa por evento explícito — sucesso, logout,
   * recomeço — via `clearMFASession()`.
   */
  getMFASession(): MFASession | null {
    return this.mfaSession;
  }

  clearMFASession() {
    this.mfaSession = null;
    this.lastSendAttemptAt = 0;
  }

  /** Retorna true se pode disparar send-code; marca o timestamp se sim. */
  tryBeginSend(): boolean {
    const now = Date.now();
    if (now - this.lastSendAttemptAt < MFAService.SEND_DEDUP_MS) {
      return false;
    }
    this.lastSendAttemptAt = now;
    return true;
  }

  /**
   * Verifica código MFA após login (Fluxo principal)
   * Endpoint: POST /auth/verify-mfa
   */
  async verifyMFACode(
    code: string,
    mfaToken: string,
    cellphone?: string,
  ): Promise<MFAVerificationResult> {
    try {
      const response = await api.post("/auth/verify-mfa", {
        mfaToken,
        code,
        cellphone,
      });

      const { accessToken, expiresAt, user } = response.data;

      if (accessToken) {
        newTokenService.setTokens(accessToken, expiresAt);
        csrfService.setFromResponse(response.data);

        const refreshTokenFromBody = response.data.refreshToken || response.data.refresh_token;
        if (refreshTokenFromBody) {
          newTokenService.setRefreshToken(refreshTokenFromBody);
        }

        return {
          success: true,
          token: accessToken,
          refreshToken: refreshTokenFromBody,
          expiresAt,
          user,
        };
      }

      return { success: false, error: "Token não recebido do servidor" };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Erro ao verificar código MFA",
        status: error.response?.status,
        retryAfterSeconds: error.response?.data?.retryAfterSeconds,
      };
    }
  }

  /**
   * Valida código MFA para fluxos alternativos ou cadastro
   * Endpoint: POST /auth/validate-code
   */
  async validateCode(
    params: {
      mfaToken: string;
      code: string;
      userId?: number;
      role_id?: number;
      status?: number;
      type?: string;
      cellphone?: string;
    }
  ): Promise<MFAVerificationResult> {
    try {
      const response = await api.post("/auth/validate-code", params);
      const data = response.data;

      // Se a autenticação for completa
      if (data.accessToken || data.token) {
        const token = data.accessToken || data.token;
        newTokenService.setTokens(token, data.expiresAt);
        csrfService.setFromResponse(data);

        const refreshTokenFromBody = data.refreshToken || data.refresh_token;
        if (refreshTokenFromBody) {
          newTokenService.setRefreshToken(refreshTokenFromBody);
        }

        return {
          success: true,
          token,
          refreshToken: refreshTokenFromBody,
          user: data.user,
          expiresAt: data.expiresAt,
        };
      }

      // Se for um passo intermediário (ex: validação de email no cadastro)
      if (data.next_step || data.nextStep) {
        return {
          success: true,
          next_step: data.next_step || data.nextStep,
          mfaSessionToken: data.mfaSessionToken || data.MFA_SESSION,
          user: data.user,
        };
      }

      return { success: false, error: "Resposta inesperada do servidor" };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Erro ao validar código",
      };
    }
  }

  /**
   * Reenvia código MFA
   * Endpoint: POST /auth/resend/sms
   */
  async resendCode(params: {
    userId: number;
    method: "sms" | "email";
    email: string;
  }) {
    try {
      const response = await api.post("/auth/resend/sms", params);
      
      if (response.data.mfaSessionToken) {
        const session = this.getMFASession();
        if (session) {
          this.setMFASession({
            ...session,
            mfaToken: response.data.mfaSessionToken,
            expiresAt: new Date(response.data.expiresAt),
          });
        }
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Erro ao reenviar código");
    }
  }

  isMFASessionValid(): boolean {
    const session = this.getMFASession();
    if (!session) return false;
    return new Date(session.expiresAt) > new Date();
  }
}

export default new MFAService();
